import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

import { ImageData } from '@/slw/entities/chat/chat.type';
import Loader from '@/slw/shared/ui/loader';

type ImageModalProps = {
  imageData: ImageData;
  closeModal: () => void;
};

export function ImageModal({ imageData, closeModal }: ImageModalProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(imageData.currentIndex || 0);

  const { src, alt, allImages } = imageData;
  const hasMultipleImages = allImages && allImages.length > 1;

  // Lấy URL ảnh hiện tại từ allImages nếu có, nếu không thì dùng src
  const currentImageSrc = hasMultipleImages ? allImages[currentImageIndex] : src;

  useEffect(() => {
    const image = new Image();

    image.src = currentImageSrc;
    image.onload = (): void => setIsLoading(false);
  }, [currentImageSrc]);

  const goToPreviousImage = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (hasMultipleImages) {
      setIsLoading(true);
      setCurrentImageIndex((prevIndex) => (prevIndex === 0 ? allImages.length - 1 : prevIndex - 1));
    }
  };

  const goToNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (hasMultipleImages) {
      setIsLoading(true);
      setCurrentImageIndex((prevIndex) => (prevIndex === allImages.length - 1 ? 0 : prevIndex + 1));
    }
  };

  return (
    <div className='fixed inset-0 z-40'>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className='fixed h-screen w-screen bg-black/80'
        onClick={closeModal}
      />
      {/* Navigation buttons */}
      {hasMultipleImages && (
        <>
          <button
            type='button'
            onClick={goToPreviousImage}
            className='absolute left-0 top-1/2 ml-4 -translate-y-1/2  bg-white/20 hover:bg-white/30 
                  rounded-full p-2 text-white z-50 transition-all'
            aria-label='Previous image'
          >
            <IoChevronBack size={24} />
          </button>
          <button
            type='button'
            onClick={goToNextImage}
            className='absolute right-0 top-1/2 -translate-y-1/2 mr-4  bg-white/20 hover:bg-white/30 
                  rounded-full p-2 text-white z-50 transition-all'
            aria-label='Next image'
          >
            <IoChevronForward size={24} />
          </button>
        </>
      )}
      <div className='flex h-full w-full items-center justify-center'>
        <div className='z-50 relative'>
          {isLoading ? (
            <motion.i
              className='text-4xl text-primary'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Loader className='animate-spin' />
            </motion.i>
          ) : (
            <motion.div
              key={currentImageSrc} // Key để React biết khi nào re-render animation
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: 1,
                scale: 1,
                transition: { type: 'spring', duration: 0.5, bounce: 0.4 },
              }}
              exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
            >
              <picture className='group relative flex max-w-3xl'>
                <source srcSet={currentImageSrc} type='image/*' />
                <img
                  className='max-h-[75vh] rounded-lg object-contain md:max-h-[80vh]'
                  src={currentImageSrc}
                  alt={alt || `Image ${currentImageIndex + 1}`}
                />
              </picture>

              {/* Image counter */}
              {hasMultipleImages && (
                <div
                  className='absolute bottom-0 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 
                  rounded-full text-sm'
                >
                  {currentImageIndex + 1} / {allImages.length}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
